/**
 * ReservationListPage - 예약 현황 리스트 뷰
 *
 * Phase 0D2: Appointment 직접 조회로 전환 (Consult 경유 제거)
 * - appointmentApi.searchAppointments() 사용
 * - KPI 카드: appointmentApi.getSummary() (기존 유지)
 * - 리스트: appointmentApi.searchAppointments() (신규)
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useScrollPosition } from "@/hooks/useScrollPosition";
import { useReservationFilters } from "@/hooks/useReservationFilters";
import dayjs from "dayjs";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  appointmentApi,
  type AppointmentDailySummary,
} from "@/api/appointment";
import BookingDetailSheet from "@/components/appointments/BookingDetailSheet";
import ManualAppointmentDialog from "@/components/appointments/ManualAppointmentDialog";
import { PrioritySelectionDialog } from "@/components/appointments/PrioritySelectionDialog";
import type { AppointmentTimeline } from "@/types/appointment";

import {
  ReservationSummaryCards,
  type SummaryCardDef,
} from "@/components/appointments/reservation-list/ReservationSummaryCards";
import { ReservationFilterBar } from "@/components/appointments/reservation-list/ReservationFilterBar";
import { ReservationTable } from "@/components/appointments/reservation-list/ReservationTable";

export default function ReservationListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  useScrollPosition({ key: "reservation-list" });

  // Filter state management (staged → applied 패턴)
  const {
    filters, // staged (UI display)
    appliedFilters, // applied (query trigger)
    applyPreset,
    applyCardFilter,
    applyCustomRange,
    setSearchQuery,
    setSortOrder,
    toggleStatus,
    clearStatusFilters,
    setStatusFiltersDirect,
    executeSearch,
    resetFilters,
  } = useReservationFilters();

  // Modal state
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentTimeline | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);
  const [priorityDialogData, setPriorityDialogData] =
    useState<AppointmentTimeline | null>(null);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // URL appointmentId: open specific appointment from external link
  const appointmentIdParam = searchParams.get("appointmentId");
  const { data: linkedAppointment } = useQuery({
    queryKey: ["appointment-by-id", appointmentIdParam],
    queryFn: () => appointmentApi.getById(Number(appointmentIdParam)),
    enabled: !!appointmentIdParam,
    staleTime: 0,
  });

  useEffect(() => {
    if (linkedAppointment && appointmentIdParam) {
      setSelectedAppointment(linkedAppointment);
      setIsModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("appointmentId");
      setSearchParams(next, { replace: true });
    }
  }, [linkedAppointment, appointmentIdParam]);

  // Phase 0D2: Appointment 직접 검색 — appliedFilters만 참조 (검색 버튼 기반)
  const { data: appointments = [], isLoading: isListLoading } = useQuery({
    queryKey: [
      "appointments-search",
      appliedFilters.statusFilters,
      dayjs(appliedFilters.dateRange.from).format("YYYY-MM-DD"),
      dayjs(appliedFilters.dateRange.to).format("YYYY-MM-DD"),
      appliedFilters.sortOrder,
      appliedFilters.searchQuery,
    ],
    queryFn: () =>
      appointmentApi.searchAppointments({
        from: dayjs(appliedFilters.dateRange.from).format("YYYY-MM-DD"),
        to: dayjs(appliedFilters.dateRange.to).format("YYYY-MM-DD"),
        status: appliedFilters.statusFilters.length > 0
          ? appliedFilters.statusFilters
          : undefined,
        q: appliedFilters.searchQuery.trim() || undefined,
        sort: appliedFilters.sortOrder,
      }),
  });

  // Monthly summary for KPI cards
  const now = dayjs();
  const { data: monthlySummary } = useQuery({
    queryKey: ["appointments-summary", now.year(), now.month() + 1],
    queryFn: () => appointmentApi.getSummary(now.year(), now.month() + 1),
  });

  const summaryMap = useMemo(() => {
    const map = new Map<string, AppointmentDailySummary>();
    monthlySummary?.forEach((s) => map.set(s.date, s));
    return map;
  }, [monthlySummary]);

  // Status counts for chips — Phase 0D2: Appointment 기반
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    appointments.forEach((a) => {
      const s = a.status;
      if (s) counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [appointments]);

  // Handlers
  const handleAppointmentClick = useCallback(
    (appointment: AppointmentTimeline) => {
      setSelectedAppointment(appointment);
      setIsModalOpen(true);
    },
    [],
  );

  const handleSummaryCardClick = useCallback(
    (cardId: string, filterAction: SummaryCardDef["filterAction"]) => {
      const isDeselecting = activeCardId === cardId;
      setActiveCardId(isDeselecting ? null : cardId);

      if (isDeselecting) {
        // 카드 해제: 기본 필터로 복원 + 즉시 조회
        resetFilters();
      } else {
        // 카드 선택: 해당 필터 즉시 적용 (stale closure 방지)
        applyCardFilter(filterAction);
      }
    },
    [activeCardId, resetFilters, applyCardFilter],
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">예약 현황</h1>
        <Button
          variant="default"
          size="sm"
          className="h-9"
          onClick={() => setIsManualDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          예약 추가
        </Button>
      </div>

      {/* KPI Summary Cards */}
      <ReservationSummaryCards
        summaryMap={summaryMap}
        activeCardId={activeCardId}
        onCardClick={handleSummaryCardClick}
      />

      {/* Filter Bar */}
      <ReservationFilterBar
        filters={filters}
        statusCounts={statusCounts}
        onPresetChange={applyPreset}
        onCustomRange={applyCustomRange}
        onSearchChange={setSearchQuery}
        onSortChange={setSortOrder}
        onStatusToggle={toggleStatus}
        onStatusClearAll={clearStatusFilters}
        onStatusSetFilters={setStatusFiltersDirect}
        onSearch={executeSearch}
        onReset={resetFilters}
      />

      {/* Main Content: Table */}
      <div className="min-w-0">
        <ReservationTable
          appointments={appointments}
          isLoading={isListLoading}
          onAppointmentClick={handleAppointmentClick}
          onPrioritySelect={setPriorityDialogData}
        />
      </div>

      {/* Modals */}
      <BookingDetailSheet
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        appointment={selectedAppointment}
      />

      <ManualAppointmentDialog
        isOpen={isManualDialogOpen}
        onClose={() => setIsManualDialogOpen(false)}
      />

      {priorityDialogData && (
        <PrioritySelectionDialog
          open={!!priorityDialogData}
          appointment={priorityDialogData}
          onConfirm={async (priority) => {
            await appointmentApi.approve(priorityDialogData.id, priority);
            setPriorityDialogData(null);
          }}
          onCancel={() => setPriorityDialogData(null)}
        />
      )}
    </div>
  );
}
